/**
 * PostHog analytics event tracking + Google Ads conversions
 */

import posthog from 'posthog-js';

// Google Ads conversion tracking
const GOOGLE_ADS_ID = 'AW-17801624024';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function trackGoogleAdsConversion(
  conversionLabel: string,
  opts?: { value?: number; email?: string; transactionId?: string }
) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const params: Record<string, unknown> = {
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
    value: opts?.value ?? 1,
    currency: 'USD',
  };

  // Dedup: the same Stripe session ID on a success-page refresh won't be
  // counted twice by Google Ads.
  if (opts?.transactionId) {
    params.transaction_id = opts.transactionId;
  }

  // Enhanced Conversions: pass plaintext email so gtag SHA-256s and submits
  // it for click-back attribution on browsers where the GCLID cookie was lost.
  const email = opts?.email?.toLowerCase().trim();
  if (email) {
    params.user_data = { email };
  }

  window.gtag('event', 'conversion', params);
}

// Google Ads conversion labels (from Google Ads dashboard).
// PURCHASE (the "Pro Subscription" action in the dashboard) is the only one
// that should be a PRIMARY conversion — it now carries the real charge amount
// for every product (Pro, Bulk, API plans, credit packs). The rest are
// engagement signals and must stay SECONDARY so bidding optimizes for revenue,
// not clicks on the copy button.
const GOOGLE_ADS_CONVERSIONS = {
  TRANSCRIPT_SUCCESS: 'xa4hCKD1idEbENjzvKhC',
  COPY_TRANSCRIPT: '0-eiCKP1idEbENjzvKhC',
  DOWNLOAD_TRANSCRIPT: 'Q0VmCKb1idEbENjzvKhC',
  PURCHASE: '7vJ6CNXJhNEbENjzvKhC',
  AI_SUMMARY: 'og9bCKqXitEbENjzvKhC',
  UPGRADE_MODAL_SHOWN: 'QhGwCOmWqdEbENjzvKhC',
  SEARCH_TRANSCRIPT: '90joCIjJ6tIbENjzvKhC',
} as const;

// Event names as constants for consistency
export const EVENTS = {
  // Core actions
  TRANSCRIPT_FETCH: 'transcript_fetch',
  TRANSCRIPT_SUCCESS: 'transcript_success',
  TRANSCRIPT_ERROR: 'transcript_error',

  // AI Summary
  SUMMARY_CLICK: 'summary_click',
  SUMMARY_SUCCESS: 'summary_success',
  SUMMARY_ERROR: 'summary_error',

  // Engagement
  COPY_TRANSCRIPT: 'copy_transcript',
  DOWNLOAD_TRANSCRIPT: 'download_transcript',
  TOGGLE_TIMESTAMPS: 'toggle_timestamps',
  SEARCH_TRANSCRIPT: 'search_transcript',

  // Conversion
  API_ACCESS_CLICK: 'api_access_click',
  WAITLIST_OPEN: 'waitlist_open',
  WAITLIST_SUBMIT: 'waitlist_submit',
  WAITLIST_SUCCESS: 'waitlist_success',

  // Limits & Upgrade funnel
  RATE_LIMIT_HIT: 'rate_limit_hit',
  LIMIT_REACHED: 'limit_reached',
  UPGRADE_MODAL_SHOWN: 'upgrade_modal_shown',
  UPGRADE_CLICKED: 'upgrade_clicked',

  // Feedback
  TRANSCRIPT_FEEDBACK: 'transcript_feedback',
  SUMMARY_FEEDBACK: 'summary_feedback',
  FEEDBACK_CLICK: 'feedback_click',
  FEEDBACK_SUBMITTED: 'feedback_submitted',

  // Auto-translate (coming soon feature interest)
  AUTO_TRANSLATE_CLICK: 'auto_translate_click',

  // Credits
  CREDITS_SECTION_SHOWN: 'credits_section_shown',
  CREDIT_PACK_CLICKED: 'credit_pack_clicked',
  CREDIT_PURCHASE_SUCCESS: 'credit_purchase_success',
  CREDIT_USED: 'credit_used',

  // VPN Sign-In
  VPN_SIGNIN_MODAL_SHOWN: 'vpn_signin_modal_shown',
  VPN_SIGNIN_SUCCESS: 'vpn_signin_success',
  VPN_SIGNIN_ERROR: 'vpn_signin_error',
} as const;

type EventName = typeof EVENTS[keyof typeof EVENTS];

interface EventProperties {
  [key: string]: string | number | boolean | undefined;
}

export function track(event: EventName, properties?: EventProperties) {
  if (typeof window !== 'undefined' && posthog) {
    posthog.capture(event, properties);
  }
}

// Convenience functions for common events
export const analytics = {
  trackTranscriptFetch: (videoId: string) => {
    track(EVENTS.TRANSCRIPT_FETCH, { video_id: videoId });
  },

  trackTranscriptSuccess: (
    videoId: string,
    wordCount: number,
    cached: boolean,
    videoTitle?: string,
    channelName?: string
  ) => {
    track(EVENTS.TRANSCRIPT_SUCCESS, {
      video_id: videoId,
      word_count: wordCount,
      cached,
      video_title: videoTitle,
      channel_name: channelName,
    });
    // Google Ads conversion - key engagement metric
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.TRANSCRIPT_SUCCESS);
  },

  trackTranscriptError: (
    videoId: string,
    error: string,
    errorCategory: string,
    extra?: Record<string, unknown>,
  ) => {
    track(EVENTS.TRANSCRIPT_ERROR, { video_id: videoId, error, error_category: errorCategory, ...extra });
  },

  trackSummaryClick: (videoId: string) => {
    track(EVENTS.SUMMARY_CLICK, { video_id: videoId });
  },

  trackSummarySuccess: (videoId: string, cached: boolean) => {
    track(EVENTS.SUMMARY_SUCCESS, { video_id: videoId, cached });
    // Google Ads conversion - premium feature engagement
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.AI_SUMMARY);
  },

  trackSummaryError: (videoId: string, error: string) => {
    track(EVENTS.SUMMARY_ERROR, { video_id: videoId, error });
  },

  trackCopyTranscript: (wordCount: number, withTimestamps: boolean) => {
    track(EVENTS.COPY_TRANSCRIPT, { word_count: wordCount, with_timestamps: withTimestamps });
    // Google Ads conversion - high-value action
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.COPY_TRANSCRIPT);
  },

  trackDownloadTranscript: (wordCount: number, withTimestamps: boolean, format: 'txt' | 'srt' | 'vtt' = 'txt') => {
    track(EVENTS.DOWNLOAD_TRANSCRIPT, { word_count: wordCount, with_timestamps: withTimestamps, format });
    // Google Ads conversion - high-value action
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.DOWNLOAD_TRANSCRIPT);
  },

  trackToggleTimestamps: (enabled: boolean) => {
    track(EVENTS.TOGGLE_TIMESTAMPS, { enabled });
  },

  trackSearchTranscript: (query: string, matchCount: number) => {
    track(EVENTS.SEARCH_TRANSCRIPT, { query_length: query.length, match_count: matchCount });
    // Google Ads conversion - engagement metric
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.SEARCH_TRANSCRIPT);
  },

  trackApiAccessClick: () => {
    track(EVENTS.API_ACCESS_CLICK);
  },

  trackWaitlistOpen: (source: 'api_access' | 'rate_limit' | 'footer' | 'auto') => {
    track(EVENTS.WAITLIST_OPEN, { source });
  },

  trackWaitlistSubmit: () => {
    track(EVENTS.WAITLIST_SUBMIT);
  },

  trackWaitlistSuccess: () => {
    track(EVENTS.WAITLIST_SUCCESS);
  },

  trackPurchase: (opts: { value: number; transactionId: string; email?: string }) => {
    // Google Ads conversion - the revenue event, fired for every paid product
    // with the actual charge amount (client-side only).
    // Note: PostHog event is tracked server-side via webhook to avoid duplicates
    trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.PURCHASE, opts);
  },

  trackRateLimitHit: (type: 'transcript' | 'summary') => {
    track(EVENTS.RATE_LIMIT_HIT, { type });
  },

  trackLimitReached: (type: 'transcript' | 'summary', used: number, limit: number) => {
    track(EVENTS.LIMIT_REACHED, { type, used, limit });
  },

  trackUpgradeModalShown: (source: 'limit' | 'pro_button' | 'ai_summary' | 'manual' | 'no_captions') => {
    track(EVENTS.UPGRADE_MODAL_SHOWN, { source });
    // Google Ads conversion - shows purchase intent
    if (GOOGLE_ADS_CONVERSIONS.UPGRADE_MODAL_SHOWN) {
      trackGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.UPGRADE_MODAL_SHOWN);
    }
  },

  trackUpgradeClicked: () => {
    track(EVENTS.UPGRADE_CLICKED);
  },

  trackTranscriptFeedback: (videoId: string, rating: 'positive' | 'negative', feedbackText?: string) => {
    track(EVENTS.TRANSCRIPT_FEEDBACK, {
      video_id: videoId,
      rating,
      feedback_text: feedbackText,
      has_text: !!feedbackText,
    });
  },

  trackSummaryFeedback: (videoId: string, rating: 'positive' | 'negative', feedbackText?: string) => {
    track(EVENTS.SUMMARY_FEEDBACK, {
      video_id: videoId,
      rating,
      feedback_text: feedbackText,
      has_text: !!feedbackText,
    });
  },

  trackFeedbackClick: () => {
    track(EVENTS.FEEDBACK_CLICK);
  },

  trackFeedbackSubmitted: (feedbackType: string, message: string, email?: string) => {
    track(EVENTS.FEEDBACK_SUBMITTED, {
      feedback_type: feedbackType,
      message,
      email: email || undefined,
      url: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });
  },

  // Auto-translate feature interest
  trackAutoTranslateClick: (targetLanguage: string, videoId?: string) => {
    track(EVENTS.AUTO_TRANSLATE_CLICK, {
      target_language: targetLanguage,
      video_id: videoId,
    });
  },

  // Credit tracking
  trackCreditsSectionShown: (source: 'limit' | 'pro_button' | 'ai_summary' | 'manual' | 'no_captions') => {
    track(EVENTS.CREDITS_SECTION_SHOWN, { source });
  },

  trackCreditPackClicked: (packId: string, credits: number, price: number) => {
    track(EVENTS.CREDIT_PACK_CLICKED, { pack_id: packId, credits, price_cents: price });
  },

  trackCreditPurchaseSuccess: (credits: number, newBalance: number) => {
    track(EVENTS.CREDIT_PURCHASE_SUCCESS, { credits_purchased: credits, new_balance: newBalance });
  },

  trackCreditUsed: (videoId: string, remainingCredits: number) => {
    track(EVENTS.CREDIT_USED, { video_id: videoId, remaining_credits: remainingCredits });
  },

  // VPN Sign-In tracking
  trackVpnSignInModalShown: () => {
    track(EVENTS.VPN_SIGNIN_MODAL_SHOWN);
  },

  trackVpnSignInSuccess: (provider: 'google' | 'apple') => {
    track(EVENTS.VPN_SIGNIN_SUCCESS, { provider });
  },

  trackVpnSignInError: (provider: 'google' | 'apple', error: string) => {
    track(EVENTS.VPN_SIGNIN_ERROR, { provider, error });
  },
};
